import React, { useState, useEffect, useMemo } from "react";
import {
	Avatar,
	Checkbox,
	Collapse,
	Divider,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { cloneDeep, uniq, isEqual } from "lodash";

import {
	arrowDownDefaultIcon,
	arrowUpDefaultIcon,
	collapseDownDefaultIcon,
	collapseUpDefaultIcon,
} from "assets/images";
import "./RequisitionItemSelection.css";
import Emitter from "utility/emitter";
import {
	RequisitionProcurementMethodGridDetail,
	RequisitionItemSelectionGridDetail,
	RequisitionSubItemSelectionGridDetail,
} from "models/Requisition/RequisitionItemSelectionGridDetail";
import {
	checkBoxCheckedIcon,
	checkboxDefaultIcon,
	radioCheckedIcon,
	radioDefaultIcon,
} from "utility/icons";
import {
	EstimationLogicType,
	ItemSourcingType,
} from "utility/enums/requisition-entry";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { Shared } from "utility/shared";
import { IProcurementMethod } from "models/ProcurementMethod/procurementMethodModel";
import ConfirmationDialog from "components/ConfirmationDialog";
import {
	ProcurementMethodUtil,
	widelyAvailability,
	widelyAvailabilityId,
} from "utility/procurementMethodSelectionUtil";
import { IFinYearRequisitionLineItem } from "models/Requisition/RequisitionConversionToBidDetail";
import useCalculateVat from "hooks/useCalculateVat";
import {
	SET_REQUISITION_SELECTION_ITEM_GRID_LIST,
	SET_UPDATED_REQUISITION_ITEM_LIST,
} from "utility/emitterEvents";

export const RequisitionItemSelection: React.FC<{
	isViewMode: boolean;
	isSupplierBidding: boolean;
}> = ({ isViewMode, isSupplierBidding }) => {
	const procurementMethodList: IProcurementMethod[] = useSelector(
		(state: RootState) => state.masterPageState.procurementMethodList
	);
	const calculateVat = useCalculateVat();
	const [panelList, setPanelList] = useState<string[]>([]);
	const [subPanelList, setSubPanelList] = useState<string[]>([]);
	const [requisitionItemList, setRequisitionItemList] = useState<
		RequisitionItemSelectionGridDetail[]
	>([]);
	const [tempReqItemList, setTempReqItemList] = useState<
		RequisitionItemSelectionGridDetail[]
	>([]); // required to manage list for split and combined sourcing type
	const [estimationLogicTypeId, setEstimationLogicTypeId] = React.useState<
		number | null
	>(null);
	const [itemSourcingTypeId, setItemSourcingTypeId] = useState<number | null>(
		null
	);
	const [showItemSourcingRadioBtn, setShowItemSourcingRadioBtn] =
		useState(false);
	const [transversalProcDialogDetail, setTransversalProcDialogDetail] =
		useState<{
			open: boolean;
			item: string;
			index: number;
			methodGroupIndex: number;
		}>({
			open: false,
			item: "",
			index: -1,
			methodGroupIndex: -1,
		}); // required for Transversal Procurement Confirmation
	const [sourcingTypeItemList, setSourcingTypeItemList] = useState<{
		splitTypeList: RequisitionItemSelectionGridDetail[];
		combineTypeList: RequisitionItemSelectionGridDetail[];
	}>({
		splitTypeList: [],
		combineTypeList: [],
	});
	const [transversalContractReqIds, setTransversalContractReqIds] = useState<
		number[]
	>([]); // required to manage list of selected items with isTransversal flag true

	useEffect(() => {
		Emitter.on(
			SET_REQUISITION_SELECTION_ITEM_GRID_LIST,
			(payload: {
				itemList: RequisitionItemSelectionGridDetail[];
				estimationLogicTypeId: number | null;
				itemSourcingTypeId: number | null;
			}) => {
				setTempReqItemList(cloneDeep(payload.itemList));
				setRequisitionItemList(payload.itemList);
				setEstimationLogicTypeId(payload.estimationLogicTypeId);
				const showItemSourcingOptions = !!(
					payload.itemList.map((i) => i.requisitionId).length === 1 &&
					uniq(payload.itemList[0].items.map((si) => si.procurementMethodId))
						.length > 1
				);
				setShowItemSourcingRadioBtn(showItemSourcingOptions);
				setItemSourcingTypeId(
					payload.itemSourcingTypeId
						? payload.itemSourcingTypeId
						: showItemSourcingOptions
						? ItemSourcingType.Split
						: null
				);
				// if is edit mode, manage item list for Combined Type
				if (
					payload.itemSourcingTypeId &&
					payload.itemSourcingTypeId === ItemSourcingType.Combined
				) {
					setSourcingTypeItemList({
						...sourcingTypeItemList,
						splitTypeList: cloneDeep([...payload.itemList]),
					});
					setCombineSourcingTypeList(cloneDeep([...payload.itemList]));
				}
			}
		);
		return () => {
			setTempReqItemList([]);
			setRequisitionItemList([]);
			setEstimationLogicTypeId(null);
			setItemSourcingTypeId(null);
			Emitter.off(SET_REQUISITION_SELECTION_ITEM_GRID_LIST, undefined);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePanelOpenClose = (id: string) => {
		if (panelList.includes(id)) {
			setPanelList(panelList.filter((p) => p !== id));
		} else {
			setPanelList(panelList.concat(id));
		}
	};

	const handleSubPanelOpenClose = (value: string) => {
		if (subPanelList.includes(value)) {
			setSubPanelList(subPanelList.filter((p) => p !== value));
		} else {
			setSubPanelList(subPanelList.concat(value));
		}
	};

	const getProcMethodForCombinedType = (
		selectedItems: RequisitionItemSelectionGridDetail[]
	) => {
		const totalAmount = getTotalConvertedAmount(selectedItems);
		const availabilityId: number =
			uniq(
				selectedItems[0].items
					.flatMap((i) => i.items)
					.filter((m) => m.isChecked)
					.map((r) => r.itemAvailabilityId)
			).length === 1
				? selectedItems[0].items
						.flatMap((i) => i.items)
						.filter((m) => m.isChecked)[0].itemAvailabilityId
				: widelyAvailabilityId;
		return (
			procurementMethodList.find(
				(p) =>
					totalAmount >= p.thresholdFrom &&
					totalAmount <= p.thresholdTo &&
					p.itemAvailabilityId === availabilityId
			)?.procurementMethodId || null
		);
	};

	const getProcMethodIdList = (
		selectedItems: RequisitionItemSelectionGridDetail[],
		transversalContractObj: {
			isTransversalItem: boolean;
			reqId: number;
		} | null = null
	) => {
		let procMethodIdList: number[] = [];
		const tempProcGrpList = selectedItems
			.flatMap((i) => i.items)
			.filter((m) => m.isChecked);
		const tempSubLineItemList = selectedItems
			.flatMap((i) => i.items)
			.flatMap((i) => i.items)
			.filter((m) => m.isChecked);
		const availabilityId: number =
			uniq(tempSubLineItemList.map((r) => r.itemAvailability)).length === 1
				? tempSubLineItemList[0].itemAvailabilityId
				: widelyAvailabilityId;
		const totalAmount = getTotalConvertedAmount(selectedItems);
		if (
			tempProcGrpList.length === 1 &&
			tempSubLineItemList.length === tempProcGrpList[0].items.length &&
			uniq(tempSubLineItemList.map((r) => r.requisitionId)).length === 1 &&
			isEqual(
				tempSubLineItemList.map((r) => r.convertableQty),
				tempSubLineItemList.map((r) => r.convertedQty)
			)
		) {
			// if single proc method checkbox is selected
			procMethodIdList =
				(transversalContractObj !== null &&
					transversalContractObj.isTransversalItem) ||
				transversalContractReqIds.includes(tempSubLineItemList[0].requisitionId)
					? [
							ProcurementMethodUtil.getTransversalProcMethod(
								procurementMethodList,
								totalAmount
							)?.procurementMethodId || 0,
					  ]
					: [Number(tempProcGrpList[0].procurementMethodId)];
		} else {
			// if multiple proc method checkbox is selected or multiple items are selected from diff proc method group
			procMethodIdList = procurementMethodList
				.filter(
					(p) =>
						totalAmount >= p.thresholdFrom &&
						totalAmount <= p.thresholdTo &&
						p.itemAvailabilityId === availabilityId
				)
				.map((p) => p.procurementMethodId);
		}
		return procMethodIdList;
	};

	// used to emit event to update bid items table data
	const updateRequisitionItemList = (
		estimationLogicTypeId: number | null,
		itemSourcingTypeId: number | null,
		selectedItems: RequisitionItemSelectionGridDetail[] = [],
		transversalContractObj: {
			isTransversalItem: boolean;
			reqId: number;
		} | null = null
	) => {
		const itemList = cloneDeep(
			selectedItems.length ? selectedItems : requisitionItemList
		);
		const tempList = itemList.filter((i) =>
			i.items.some((pmItem) =>
				pmItem.items.some((si) => si.isChecked && si.convertedQty > 0)
			)
		);
		if (tempList.length > 0) {
			tempList.map((t) =>
				t.items.forEach(
					(pmItem) =>
						(pmItem.items = pmItem.items.filter(
							(si) => si.isChecked && si.convertedQty > 0
						))
				)
			);
			Emitter.emit(SET_UPDATED_REQUISITION_ITEM_LIST, {
				itemList: tempList,
				estBidTotal: getTotalConvertedAmount(itemList),
				estimationLogicTypeId: estimationLogicTypeId,
				itemSourcingTypeId: itemSourcingTypeId,
				procurementMethodId: getProcMethodIdList(
					cloneDeep(tempList),
					transversalContractObj
				),
			});
		} else {
			Emitter.emit(SET_UPDATED_REQUISITION_ITEM_LIST, {
				itemList: [],
				estBidTotal: 0,
				estimationLogicTypeId: estimationLogicTypeId,
				itemSourcingTypeId: itemSourcingTypeId,
				procurementMethodId: [],
			});
		}
		if (transversalContractObj !== null) {
			updateTransversalContractReqIdList(
				transversalContractObj.isTransversalItem,
				transversalContractObj.reqId
			);
		}
	};

	const updateEstimationLogicTypeId = (
		itemList: RequisitionItemSelectionGridDetail[],
		transversalContractObj: {
			isTransversalItem: boolean;
			reqId: number;
		} | null = null
	) => {
		const tempEstimationLogicTypeId =
			itemList
				.flatMap((i) => i.items)
				.filter((i) => i.items.some((s) => s.isChecked)).length <= 1
				? null
				: estimationLogicTypeId || EstimationLogicType.Average;
		if (tempEstimationLogicTypeId !== estimationLogicTypeId) {
			setEstimationLogicTypeId(tempEstimationLogicTypeId);
		}
		updateRequisitionItemList(
			tempEstimationLogicTypeId,
			itemSourcingTypeId,
			[],
			transversalContractObj
		);
	};

	const resetConvertedQtyAndLineTotal = (
		items: RequisitionSubItemSelectionGridDetail[]
	) => {
		items.forEach((item) => {
			item.convertedQty = item.convertableQty;
			item.subTotal = item.convertableQty * item.estUnitCost;
			item.vat = calculateVat.getVatAmount(item.subTotal, !!item.vat);
			item.lineItemDetailList.forEach((lineItem) => {
				lineItem.convertedQty = lineItem.convertableQty;
				lineItem.subTotal = lineItem.convertableQty * lineItem.estUnitCost;
				lineItem.vat = calculateVat.getVatAmount(
					lineItem.subTotal,
					!!lineItem.vat
				);
			});
		});
		return items;
	};

	const handleRequisitionItemSelection = (
		isChecked: boolean,
		index: number,
		methodGroupIndex: number
	) => {
		const itemList = [...requisitionItemList];
		let subItemList = itemList[index].items[methodGroupIndex];
		subItemList.isChecked = isChecked;
		subItemList.items.map((i) => (i.isChecked = isChecked));
		if (!isChecked) {
			subItemList.items = resetConvertedQtyAndLineTotal(subItemList.items);
		}
		setRequisitionItemList(itemList);
		if (isChecked && !panelList.includes(itemList[index].requisitionNumber)) {
			setPanelList(panelList.concat(itemList[index].requisitionNumber));
		}
		if (itemSourcingTypeId === ItemSourcingType.Combined) {
			if (isChecked) {
				itemList[0].items[0].procurementMethod =
					procurementMethodList.find(
						(p) =>
							p.procurementMethodId === getProcMethodForCombinedType(itemList)
					)?.procurementMethodCategory || "";
				itemList[0].items[0].procurementMethodId =
					getProcMethodForCombinedType(itemList) || null;
				setRequisitionItemList(itemList);
			} else {
				itemList[0].items[0].procurementMethod = "";
				itemList[0].items[0].procurementMethodId = null;
				setRequisitionItemList(itemList);
			}
		}
		updateEstimationLogicTypeId(
			itemList,
			!isChecked &&
				transversalContractReqIds.findIndex(
					(i) => i === itemList[index].requisitionId
				) > -1
				? { isTransversalItem: false, reqId: itemList[index].requisitionId }
				: null
		);
	};

	const handleRequisitionSubItemSelection = (
		isChecked: boolean,
		index: number,
		methodGroupIndex: number,
		lineItemIndex: number
	) => {
		const itemList = [...requisitionItemList];
		let subItem = itemList[index].items[methodGroupIndex].items[lineItemIndex];
		if (
			isChecked &&
			showItemSourcingRadioBtn &&
			subItem.isTransversalContract
		) {
			setTransversalProcDialogDetail({
				open: true,
				item: subItem.item,
				index: index,
				methodGroupIndex: methodGroupIndex,
			}); // open Transversal Procurement Confirmation popup
		}
		subItem.isChecked = isChecked;
		const tempLineItemList = itemList[index].items[methodGroupIndex];
		tempLineItemList.isChecked =
			tempLineItemList.items.filter((i) => i.isChecked).length ===
			tempLineItemList.items.length;
		if (!isChecked) {
			subItem = resetConvertedQtyAndLineTotal([subItem])[0];
		}
		if (itemSourcingTypeId === ItemSourcingType.Combined) {
			if (itemList[0].items[0].items.filter((i) => i.isChecked).length > 0) {
				itemList[0].items[0].procurementMethod =
					procurementMethodList.find(
						(p) =>
							p.procurementMethodId === getProcMethodForCombinedType(itemList)
					)?.procurementMethodCategory || "";
				itemList[0].items[0].procurementMethodId =
					getProcMethodForCombinedType(itemList) || null;
				setRequisitionItemList(itemList);
			} else {
				itemList[0].items[0].procurementMethod = "";
				itemList[0].items[0].procurementMethodId = null;
				setRequisitionItemList(itemList);
			}
		}
		setRequisitionItemList(itemList);
		updateEstimationLogicTypeId(
			itemList,
			!isChecked && subItem.isTransversalContract
				? { isTransversalItem: false, reqId: subItem.requisitionId }
				: null
		);
	};

	const updateLineItemQty = (
		reqItemIndex: number,
		methodGroupIndex: number,
		lineItemIndex: number,
		index: number,
		incrementValue: boolean,
		changeEventUpdate = false,
		value = ""
	) => {
		const itemList = [...requisitionItemList];
		const subItem =
			itemList[reqItemIndex].items[methodGroupIndex].items[lineItemIndex]
				.lineItemDetailList[index];
		if (changeEventUpdate) {
			if (Number(value) > subItem.convertableQty || isNaN(Number(value)))
				return;
			subItem.convertedQty = Number(value);
		} else {
			const currentValue = subItem.convertedQty;
			if (
				incrementValue
					? currentValue + 1 > subItem.convertableQty
					: currentValue <= 0
			) {
				return;
			}
			subItem.convertedQty = incrementValue
				? currentValue + 1
				: currentValue - 1;
		}
		subItem.subTotal = subItem.convertedQty * subItem.estUnitCost;
		if (subItem.convertedQty > 0) {
			subItem.vat = calculateVat.getVatAmount(subItem.subTotal, !!subItem.vat);
		}
		const lineItem =
			itemList[reqItemIndex].items[methodGroupIndex].items[lineItemIndex];
		lineItem.convertedQty = lineItem.lineItemDetailList.reduce(
			(prev, curr) => prev + curr.convertedQty,
			0
		);
		lineItem.subTotal = lineItem.convertedQty * lineItem.estUnitCost;
		if (lineItem.convertedQty > 0) {
			lineItem.vat = calculateVat.getVatAmount(
				lineItem.subTotal,
				!!lineItem.vat
			);
		}
		if (itemSourcingTypeId === ItemSourcingType.Combined) {
			itemList[0].items[0].procurementMethod =
				procurementMethodList.find(
					(p) =>
						p.procurementMethodId === getProcMethodForCombinedType(itemList)
				)?.procurementMethodCategory || "";
			itemList[0].items[0].procurementMethodId =
				getProcMethodForCombinedType(itemList) || null;
		}
		setRequisitionItemList(itemList);
		updateRequisitionItemList(estimationLogicTypeId, itemSourcingTypeId);
	};

	const getTotalConvertedAmount = (
		selectedItems: RequisitionItemSelectionGridDetail[] = []
	) => {
		const subItemList = (
			selectedItems.length ? selectedItems : requisitionItemList
		)
			.flatMap((i) => i.items)
			.flatMap((i) => i.items);
		return subItemList.reduce((prevValue, currentValue) => {
			return (
				prevValue +
				(currentValue.isChecked
					? currentValue.convertedQty * currentValue.estUnitCost +
					  calculateVat.getVatAmount(currentValue.subTotal, !!currentValue.vat)
					: 0)
			);
		}, 0);
	};

	const getTotalAmount = (index: number, type: string) => {
		const subItems = requisitionItemList[index].items.flatMap((i) => i.items);
		let originalAmt = subItems.reduce((prevValue, currentValue) => {
			return (
				prevValue +
				currentValue.convertableQty * currentValue.estUnitCost +
				calculateVat.getVatAmount(
					currentValue.convertableQty * currentValue.estUnitCost,
					!!currentValue.vat
				)
			);
		}, 0);
		let convertedAmt = subItems.reduce((prevValue, currentValue) => {
			return (
				prevValue +
				(currentValue.isChecked
					? currentValue.convertedQty * currentValue.estUnitCost +
					  calculateVat.getVatAmount(currentValue.subTotal, !!currentValue.vat)
					: 0)
			);
		}, 0);
		return type === "original"
			? Shared.getFormattedCurrency(originalAmt)
			: type === "converted"
			? Shared.getFormattedCurrency(convertedAmt)
			: Shared.getFormattedCurrency(originalAmt - convertedAmt);
	};

	const handleEstimationLogicTypeChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setEstimationLogicTypeId(Number(event.target.value));
		updateRequisitionItemList(Number(event.target.value), itemSourcingTypeId);
	};

	const disableEstimationLogicType = useMemo(
		() =>
			requisitionItemList.filter((i) =>
				i.items.some((j) => j.items.some((k) => k.isChecked))
			).length <= 1,
		[requisitionItemList]
	);

	const disableItemSourcingType = useMemo(() => false, []);

	const isMethodSelectionDisabled = (
		methodGrpDetail: RequisitionProcurementMethodGridDetail
	) => {
		if (
			showItemSourcingRadioBtn &&
			itemSourcingTypeId &&
			itemSourcingTypeId === ItemSourcingType.Combined
		) {
			return false;
		}
		const tempList = requisitionItemList
			.flatMap((i) => i.items)
			.filter((m) => m.isChecked);
		const tempLineItemList = requisitionItemList
			.flatMap((i) => i.items)
			.flatMap((i) => i.items)
			.filter((m) => m.isChecked);
		return methodGrpDetail.isChecked
			? false
			: tempList.length > 0
			? tempList.filter(
					(t) => t.procurementMethodId !== methodGrpDetail.procurementMethodId
			  ).length > 0
			: tempLineItemList.length > 0
			? tempLineItemList.filter(
					(t) => t.procurementMethodId !== methodGrpDetail.procurementMethodId
			  ).length > 0
			: false;
	};

	const setCombineSourcingTypeList = (
		tempList: RequisitionItemSelectionGridDetail[],
		isUpdateReqItemList = false // if true, emit event to update list in BidWrapper
	) => {
		const subItems = tempList.flatMap((i) => i.items).flatMap((i) => i.items);
		const reqItemList: RequisitionItemSelectionGridDetail[] = [
			{
				requisitionId: tempList[0].requisitionId,
				requisitionNumber: tempList[0].requisitionNumber,
				items: [
					{
						...new RequisitionProcurementMethodGridDetail(),
						isChecked: false,
						items: subItems,
					},
				],
			},
		];
		reqItemList[0].items[0].itemAvailability =
			uniq(subItems.map((si) => si.itemAvailability)).length === 1
				? subItems[0].itemAvailability
				: widelyAvailability;
		reqItemList[0].items[0].isChecked = reqItemList[0].items[0].items.every(
			(i) => i.isChecked
		);
		if (!isUpdateReqItemList || !reqItemList[0].items[0].procurementMethodId) {
			reqItemList[0].items[0].procurementMethod =
				procurementMethodList.find(
					(p) =>
						p.procurementMethodId === getProcMethodForCombinedType(reqItemList)
				)?.procurementMethodCategory || "";
			reqItemList[0].items[0].procurementMethodId =
				getProcMethodForCombinedType(reqItemList) || null;
		}
		setRequisitionItemList(reqItemList);
		if (isUpdateReqItemList)
			updateRequisitionItemList(
				estimationLogicTypeId,
				ItemSourcingType.Combined,
				reqItemList
			);
	};

	const handleItemSourcingTypeChange = (typeId: number) => {
		const tempList: RequisitionItemSelectionGridDetail[] = cloneDeep([
			...requisitionItemList,
		]);
		setItemSourcingTypeId(typeId);
		if (typeId === ItemSourcingType.Combined) {
			setSourcingTypeItemList({
				...sourcingTypeItemList,
				splitTypeList: cloneDeep([...tempList]),
			});
			if (sourcingTypeItemList.combineTypeList.length === 0)
				setCombineSourcingTypeList(cloneDeep([...tempReqItemList]), true);
			else {
				setRequisitionItemList(sourcingTypeItemList.combineTypeList);
				updateRequisitionItemList(
					estimationLogicTypeId,
					typeId,
					sourcingTypeItemList.combineTypeList
				);
			}
		}
		if (typeId === ItemSourcingType.Split) {
			setSourcingTypeItemList({
				...sourcingTypeItemList,
				combineTypeList: cloneDeep([...tempList]),
			});
			setRequisitionItemList(sourcingTypeItemList.splitTypeList);
			updateRequisitionItemList(
				estimationLogicTypeId,
				typeId,
				sourcingTypeItemList.splitTypeList
			);
		}
	};

	const updateTransversalContractReqIdList = (
		isTransversalItem: boolean,
		reqId = 0
	) => {
		const index = transversalContractReqIds.findIndex((i) => i === reqId);
		if (isTransversalItem && index === -1) {
			setTransversalContractReqIds([...transversalContractReqIds, reqId]);
		} else if (index > -1) {
			setTransversalContractReqIds(
				transversalContractReqIds.filter((i) => i !== reqId)
			);
		}
	};

	const handleTransversalProcConfirmationDialogClose = (
		isConfirm: boolean | undefined
	) => {
		const itemList = [...requisitionItemList];
		if (
			!itemList[transversalProcDialogDetail.index].items[
				transversalProcDialogDetail.methodGroupIndex
			].procurementMethodId
		) {
			if (isConfirm) {
				const transProcMethod = ProcurementMethodUtil.getTransversalProcMethod(
					procurementMethodList,
					getTotalConvertedAmount(itemList)
				);
				itemList[transversalProcDialogDetail.index].items[
					transversalProcDialogDetail.methodGroupIndex
				].procurementMethodId = transProcMethod?.procurementMethodId || null;
				itemList[transversalProcDialogDetail.index].items[
					transversalProcDialogDetail.methodGroupIndex
				].procurementMethod = transProcMethod?.procurementMethodCategory || "";
			} else {
				itemList[transversalProcDialogDetail.index].items[
					transversalProcDialogDetail.methodGroupIndex
				].procurementMethodId = getProcMethodForCombinedType(itemList);
				itemList[transversalProcDialogDetail.index].items[
					transversalProcDialogDetail.methodGroupIndex
				].procurementMethod =
					procurementMethodList.find(
						(p) =>
							p.procurementMethodId === getProcMethodForCombinedType(itemList)
					)?.procurementMethodCategory || "";
			}
			setRequisitionItemList(itemList);
		}
		updateRequisitionItemList(
			estimationLogicTypeId,
			itemSourcingTypeId,
			itemList,
			{
				isTransversalItem: true,
				reqId:
					requisitionItemList[transversalProcDialogDetail.methodGroupIndex]
						.requisitionId,
			}
		);
		setTransversalProcDialogDetail({
			open: false,
			item: "",
			index: -1,
			methodGroupIndex: -1,
		});
	};

	const bindReqLineDetailByFinancialYear = (
		requisitionLineItem: RequisitionSubItemSelectionGridDetail,
		reqItemIndex: number,
		methodGroupIndex: number,
		lineItemIndex: number
	) => {
		return (
			<Table style={{ width: "100%", tableLayout: "fixed" }}>
				<TableBody className="fin-year-detail-tbody">
					{requisitionLineItem.lineItemDetailList.map(
						(lineItem: IFinYearRequisitionLineItem, index: number) => (
							<TableRow key={lineItem.requisitionLineDetailId} className="h-45">
								<TableCell className="w-80"></TableCell>
								<TableCell className="w-80">{lineItem.financialYear}</TableCell>
								<TableCell align="center" className="w-147 cell-content-space">
									<Grid container>
										{!requisitionLineItem.isChecked && (
											<Grid item xs={12}>
												<Typography variant="body1">{`${lineItem.convertedQty} / ${lineItem.convertableQty}`}</Typography>
											</Grid>
										)}
										{requisitionLineItem.isChecked && (
											<>
												<Grid item xs={8}>
													<TextField
														value={lineItem.convertedQty}
														variant="outlined"
														autoComplete="off"
														className="number-text-field qty-selection-field"
														onChange={(event) =>
															updateLineItemQty(
																reqItemIndex,
																methodGroupIndex,
																lineItemIndex,
																index,
																false,
																true,
																event.target.value
															)
														}
														disabled={isViewMode}
														InputProps={{
															endAdornment: (
																<InputAdornment position="end">
																	<div className="action-wrapper">
																		<IconButton
																			edge="end"
																			onClick={() =>
																				updateLineItemQty(
																					reqItemIndex,
																					methodGroupIndex,
																					lineItemIndex,
																					index,
																					true
																				)
																			}
																			disabled={isViewMode}
																		>
																			<Avatar
																				src={arrowUpDefaultIcon}
																				alt="Up"
																			/>
																		</IconButton>
																		<IconButton
																			edge="end"
																			onClick={() =>
																				updateLineItemQty(
																					reqItemIndex,
																					methodGroupIndex,
																					lineItemIndex,
																					index,
																					false
																				)
																			}
																			disabled={isViewMode}
																		>
																			<Avatar
																				src={arrowDownDefaultIcon}
																				alt="Down"
																			/>
																		</IconButton>
																	</div>
																</InputAdornment>
															),
														}}
													/>
												</Grid>
												<Grid item xs={4}>
													<Typography
														variant="body1"
														mt={1}
													>{`/ ${lineItem.convertableQty}`}</Typography>
												</Grid>
											</>
										)}
									</Grid>
								</TableCell>
								<TableCell className="w-100 cell-content-space">
									{Shared.getFormattedCurrency(
										lineItem.subTotal > 0 ? lineItem.subTotal + lineItem.vat : 0
									)}
								</TableCell>
							</TableRow>
						)
					)}
				</TableBody>
			</Table>
		);
	};

	return (
		<div className="item-selection-panel">
			<div className="item-selection-header">
				<Typography variant="h4">Item Selection</Typography>
				{requisitionItemList.length > 0 ? (
					<div className="content">
						{!showItemSourcingRadioBtn && (
							<FormControl className="radio-btn">
								<FormLabel id="demo-row-radio-buttons-group-label">
									Varying Estimation Logic
								</FormLabel>
								<RadioGroup
									row
									value={estimationLogicTypeId}
									onChange={handleEstimationLogicTypeChange}
								>
									<FormControlLabel
										value={EstimationLogicType.Average}
										control={
											<Radio
												icon={radioDefaultIcon}
												checkedIcon={radioCheckedIcon}
												disabled={disableEstimationLogicType || isViewMode}
											/>
										}
										label="Average"
									/>
									<FormControlLabel
										value={EstimationLogicType.Least}
										control={
											<Radio
												icon={radioDefaultIcon}
												checkedIcon={radioCheckedIcon}
												disabled={disableEstimationLogicType || isViewMode}
											/>
										}
										label="Least"
									/>
								</RadioGroup>
							</FormControl>
						)}
						{showItemSourcingRadioBtn && (
							<FormControl className="radio-btn">
								<FormLabel id="demo-row-radio-buttons-group-label">
									Item Sourcing
								</FormLabel>
								<RadioGroup
									row
									value={itemSourcingTypeId}
									onChange={(event) =>
										handleItemSourcingTypeChange(Number(event.target.value))
									}
								>
									<FormControlLabel
										value={ItemSourcingType.Split}
										control={
											<Radio
												icon={radioDefaultIcon}
												checkedIcon={radioCheckedIcon}
												disabled={disableItemSourcingType || isViewMode}
											/>
										}
										label="Split"
										disabled={isViewMode}
									/>
									<FormControlLabel
										value={ItemSourcingType.Combined}
										control={
											<Radio
												icon={radioDefaultIcon}
												checkedIcon={radioCheckedIcon}
												disabled={disableItemSourcingType || isViewMode}
											/>
										}
										label="Combined"
									/>
								</RadioGroup>
							</FormControl>
						)}
						<div className="text-label-wrapper">
							<Typography variant="body1" className="light-grey-text">
								Total converted
							</Typography>
							<Typography variant="h4" className="default-text">
								{Shared.getFormattedCurrency(getTotalConvertedAmount())}
							</Typography>
						</div>
					</div>
				) : (
					<>
						<Divider />
						<Typography
							className="content"
							variant="body1"
							justifyContent="center"
							pt={2}
						>
							No Data Available
						</Typography>
					</>
				)}
			</div>
			{requisitionItemList.map(
				(
					requisitionItem: RequisitionItemSelectionGridDetail,
					index: number
				) => (
					<TableContainer
						component={Paper}
						className="item-selection-table"
						key={requisitionItem.requisitionId}
					>
						<div className="table-responsive">
							<Table className="item-collapsible-table">
								<TableHead
									style={{ visibility: index > 0 ? "collapse" : "visible" }}
								>
									<TableRow>
										<TableCell></TableCell>
										<TableCell></TableCell>
										<TableCell></TableCell>
										<TableCell>Original Amt</TableCell>
										<TableCell>Converted Amt</TableCell>
										<TableCell>Remaining Amt</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<React.Fragment>
										<TableRow className="collapsible-row h-45">
											<TableCell>
												<IconButton
													className="collapse-icon"
													size="small"
													onClick={() =>
														handlePanelOpenClose(
															requisitionItem.requisitionNumber
														)
													}
												>
													{panelList.includes(
														requisitionItem.requisitionNumber
													) ? (
														<Avatar src={collapseUpDefaultIcon} alt="Up Icon" />
													) : (
														<Avatar
															src={collapseDownDefaultIcon}
															alt="Down Icon"
														/>
													)}
												</IconButton>
											</TableCell>
											<TableCell></TableCell>
											<TableCell className="req-id">
												{requisitionItem.requisitionNumber}
											</TableCell>
											<TableCell>{getTotalAmount(index, "original")}</TableCell>
											<TableCell>
												{getTotalAmount(index, "converted")}
											</TableCell>
											<TableCell>
												{getTotalAmount(index, "remaining")}
											</TableCell>
										</TableRow>
										{requisitionItem.items.map(
											(
												prMethodDetail: RequisitionProcurementMethodGridDetail,
												methodGroupIndex: number
											) => (
												<TableRow
													key={prMethodDetail.procurementMethodId}
													className="method-detail-row"
												>
													<TableCell style={{ padding: 0 }} colSpan={6}>
														<Collapse
															in={panelList.includes(
																requisitionItem.requisitionNumber
															)}
															timeout="auto"
															unmountOnExit
														>
															<Table>
																<TableHead
																	style={{
																		visibility:
																			methodGroupIndex > 0
																				? "collapse"
																				: "visible",
																	}}
																>
																	<TableRow className="h-35">
																		<TableCell></TableCell>
																		<TableCell></TableCell>
																		<TableCell>Procurement Method</TableCell>
																		<TableCell>No. of Items</TableCell>
																	</TableRow>
																</TableHead>
																<TableBody>
																	<React.Fragment>
																		<TableRow className="collapsible-row h-45">
																			<TableCell>
																				<IconButton
																					className="collapse-icon"
																					size="small"
																					onClick={() =>
																						handleSubPanelOpenClose(
																							`${methodGroupIndex}-${requisitionItem.requisitionNumber}-${itemSourcingTypeId}`
																						)
																					}
																				>
																					{subPanelList.includes(
																						`${methodGroupIndex}-${requisitionItem.requisitionNumber}-${itemSourcingTypeId}`
																					) ? (
																						<Avatar
																							src={collapseUpDefaultIcon}
																							alt="Up Icon"
																						/>
																					) : (
																						<Avatar
																							src={collapseDownDefaultIcon}
																							alt="Down Icon"
																						/>
																					)}
																				</IconButton>
																			</TableCell>
																			<TableCell>
																				<Checkbox
																					className="custom-checkbox"
																					checkedIcon={checkBoxCheckedIcon}
																					icon={checkboxDefaultIcon}
																					checked={prMethodDetail.isChecked}
																					onChange={(event) => {
																						handleRequisitionItemSelection(
																							event.target.checked,
																							index,
																							methodGroupIndex
																						);
																					}}
																					// disabled={
																					// 	isMethodSelectionDisabled(
																					// 		prMethodDetail
																					// 	) || isViewMode
																					// }
																					disabled={isSupplierBidding}
																				/>
																			</TableCell>
																			<TableCell className="req-id">
																				{prMethodDetail.procurementMethod ||
																					"-"}
																			</TableCell>
																			<TableCell className="req-id">
																				{prMethodDetail.items.length}
																			</TableCell>
																		</TableRow>
																		<TableRow>
																			<TableCell
																				style={{ padding: 0 }}
																				colSpan={6}
																			>
																				<Collapse
																					in={subPanelList.includes(
																						`${methodGroupIndex}-${requisitionItem.requisitionNumber}-${itemSourcingTypeId}`
																					)}
																					timeout="auto"
																					unmountOnExit
																				>
																					<Table className="fixed-table-layout">
																						<TableHead>
																							<TableRow className="h-35">
																								<TableCell className="w-44"></TableCell>
																								<TableCell className="w-28"></TableCell>
																								<TableCell
																									align="center"
																									className="w-60"
																								>
																									Dept.
																								</TableCell>
																								<TableCell
																									align="center"
																									className="w-100"
																								>
																									Item
																								</TableCell>
																								<TableCell
																									align="center"
																									className="w-80"
																								>
																									Qty
																								</TableCell>
																								<TableCell className="w-100">
																									Line Total
																								</TableCell>
																							</TableRow>
																						</TableHead>
																						<TableBody>
																							{prMethodDetail.items.map(
																								(
																									subItem: RequisitionSubItemSelectionGridDetail,
																									lineItemIndex: number
																								) => (
																									<React.Fragment
																										key={
																											subItem.requisitionLineId
																										}
																									>
																										<TableRow className="h-45">
																											<TableCell></TableCell>
																											<TableCell>
																												<Checkbox
																													className="custom-checkbox"
																													checkedIcon={
																														checkBoxCheckedIcon
																													}
																													icon={
																														checkboxDefaultIcon
																													}
																													checked={
																														subItem.isChecked
																													}
																													onChange={(event) => {
																														handleRequisitionSubItemSelection(
																															event.target
																																.checked,
																															index,
																															methodGroupIndex,
																															lineItemIndex
																														);
																													}}
																													// disabled={
																													// 	isMethodSelectionDisabled(
																													// 		prMethodDetail
																													// 	) || isViewMode
																													// }
																												/>
																											</TableCell>
																											<TableCell
																												align="center"
																												className="cell-content-space"
																											>
																												{subItem.department}
																											</TableCell>
																											<TableCell
																												align="center"
																												className="cell-content-space"
																											>
																												{subItem.item}
																											</TableCell>
																											<TableCell
																												align="center"
																												className="cell-content-space"
																											>
																												<Typography variant="body1">{`${subItem.convertedQty} / ${subItem.convertableQty}`}</Typography>
																											</TableCell>
																											<TableCell className="cell-content-space fw-500">
																												{Shared.getFormattedCurrency(
																													subItem.subTotal > 0
																														? subItem.subTotal +
																																subItem.vat
																														: 0
																												)}
																											</TableCell>
																										</TableRow>
																										<TableRow>
																											<TableCell
																												style={{ padding: 0 }}
																												colSpan={6}
																											>
																												{bindReqLineDetailByFinancialYear(
																													subItem,
																													index,
																													methodGroupIndex,
																													lineItemIndex
																												)}
																											</TableCell>
																										</TableRow>
																									</React.Fragment>
																								)
																							)}
																						</TableBody>
																					</Table>
																				</Collapse>
																			</TableCell>
																		</TableRow>
																	</React.Fragment>
																</TableBody>
															</Table>
														</Collapse>
													</TableCell>
												</TableRow>
											)
										)}
									</React.Fragment>
								</TableBody>
							</Table>
						</div>
					</TableContainer>
				)
			)}
			{transversalProcDialogDetail.open && (
				<ConfirmationDialog
					open={transversalProcDialogDetail.open}
					positiveActionText="Transversal"
					negativeActionText="Assign"
					title="Transversal Procurement Confirmation"
					message={`There is an existing transversal contract for the selected item ${transversalProcDialogDetail.item}. Click the Transversal button to use the transversal contract. Or, click the Assign button to ignore the transversal contract and use the system-assigned method.`}
					handleDialogClose={handleTransversalProcConfirmationDialogClose}
				/>
			)}
		</div>
	);
};
